import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Save, Clock, X, Check } from 'lucide-react';
import { MemoryAdmissionLine } from './MemoryAdmission';

/**
 * VoicePrivacyModal — Private by Default
 * 
 * Shown after a voice conversation ends.
 * User chooses: Save Transcript or Let It Disappear
 * 
 * PRIVACY PHILOSOPHY:
 * - Conversations belong to the user
 * - Transcripts are ephemeral by default (6-hour retention)
 * - Relationship memory persists regardless
 * 
 * UX FLOW (December 2025 Revision):
 * 1. User makes decision (Save or Let It Disappear)
 * 2. Show inline confirmation with countdown
 * 3. Auto-close after 3 seconds (comfortable reading time)
 * 4. User can dismiss early with Continue button
 */

const VoicePrivacyModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  onLetExpire,
  transcriptInfo = {},
  isLoading = false,
  // 3.9 — set only when the record actually changed during this session.
  memoryAdmission = null,
  lang = null
}) => {
  // The recovery offer can mount without a lang prop, so fall back to the member's stored choice.
  const confirmLang = lang || (typeof localStorage !== 'undefined' ? localStorage.getItem('arvexis_lang') : null) || 'en';
  const [decision, setDecision] = useState(null);
  const [countdown, setCountdown] = useState(3);
  // The parent surfaces re-render frequently (the presence screen animates continuously), so a
  // fresh onClose identity on every frame used to restart the countdown interval before it could
  // ever tick — the confirmation sat on "Closing in 3..." forever and the modal never handed
  // control back. Holding onClose in a ref keeps the countdown independent of render churn.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  
  const { 
    message_count = 0, 
    duration_seconds = 0,
    expires_at = null,
    interrupted = false
  } = transcriptInfo;
  
  // Each opening is a fresh decision. Without this reset a second offer (a chained
  // interrupted Talk) would open straight into the previous confirmation screen.
  useEffect(() => {
    if (isOpen) {
      setDecision(null);
      setCountdown(3);
    }
  }, [isOpen]);
  
  // Handle countdown after decision is made
  useEffect(() => {
    if (decision && !isLoading) {
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            onCloseRef.current?.();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [decision, isLoading]);
  
  // Format duration nicely
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs} seconds`;
    if (secs === 0) return `${mins} minute${mins !== 1 ? 's' : ''}`;
    return `${mins}m ${secs}s`;
  };
  
  const handleSave = async () => {
    setDecision('save');
    setCountdown(3);
    await onSave();
  };
  
  const handleLetExpire = async () => {
    setDecision('expire');
    setCountdown(3);
    await onLetExpire();
  };
  
  if (!isOpen) return null;
  
  // Show confirmation state after decision
  if (decision && !isLoading) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-zinc-900 rounded-2xl max-w-sm w-full shadow-2xl border border-zinc-800 overflow-hidden"
          >
            <div className="p-8 text-center">
              {/* Success icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", damping: 15, stiffness: 200, delay: 0.1 }}
                className={`
                  w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center
                  ${decision === 'save' ? 'bg-indigo-500/15' : 'bg-zinc-800'}
                `}
              >
                <Check className={`w-6 h-6 ${decision === 'save' ? 'text-indigo-400' : 'text-zinc-400'}`} />
              </motion.div>
              
              {/* Confirmation message — a kept Talk lands in Saved Talks, which is the only
                  surface that reads saved transcripts. Naming any other destination would be
                  untrue to the member. */}
              <h3
                className="text-lg font-medium text-white mb-2"
                data-testid="voice-privacy-confirm-title"
              >
                {decision === 'save'
                  ? (confirmLang === 'es' ? 'Charla guardada' : 'Talk saved')
                  : 'Gone'}
              </h3>
              <p
                className="text-sm text-zinc-400 mb-6"
                data-testid="voice-privacy-confirm-body"
              >
                {decision === 'save' 
                  ? (confirmLang === 'es'
                      ? 'Esta conversación ya está disponible en Charlas Guardadas. Puedes eliminarla allí en cualquier momento.'
                      : 'This conversation is now available in Saved Talks. You can delete it there at any time.')
                  : 'The recording is deleted. Your relationship with Arvexis continues.'}
              </p>
              
              {/* Continue button + countdown */}
              <button
                onClick={onClose}
                className="px-6 py-2.5 rounded-xl text-sm font-medium bg-zinc-800 text-white hover:bg-zinc-700 transition-colors"
              >
                Continue
              </button>
              
              {/* Subtle countdown indicator */}
              <p className="text-xs text-zinc-600 mt-4">
                Closing in {countdown}...
              </p>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-zinc-900 rounded-2xl max-w-md w-full shadow-2xl border border-zinc-800 overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-zinc-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-indigo-500/10">
                  <Shield className="w-5 h-5 text-indigo-400" />
                </div>
                <h2 className="text-lg font-medium text-white">
                  Private by Default
                </h2>
              </div>
              <button 
                onClick={onClose}
                className="p-2 rounded-full hover:bg-zinc-800 transition-colors"
              >
                <X className="w-4 h-4 text-zinc-500" />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            {interrupted && (
              <p
                data-testid="voice-privacy-interrupted-note"
                className="text-zinc-300 text-sm leading-relaxed"
              >
                This one got cut off before you could decide about it. Here it is.
              </p>
            )}
            {/* Conversation stats */}
            {(message_count > 0 || duration_seconds > 0) && (
              <div className="flex items-center gap-4 text-sm text-zinc-400">
                {duration_seconds > 0 && (
                  <span>{formatDuration(duration_seconds)}</span>
                )}
                {message_count > 0 && (
                  <span>{message_count} messages</span>
                )}
              </div>
            )}
            
            {/* Privacy message */}
            <div className="text-zinc-300 text-sm leading-relaxed space-y-3">
              <p>
                This conversation is private by default. If you don&apos;t decide now, it will
                remain available for about 6 hours in case you&apos;d like to revisit it, then
                disappear on its own. If you choose to let it disappear, it&apos;s deleted right away.
              </p>
              <p className="text-zinc-400">
                Deleting the transcript doesn&apos;t erase your relationship with Arvexis. 
                She continues remembering what she learns about you so your relationship 
                can naturally grow over time. 
                <span className="text-zinc-300"> The conversation is deleted, not the relationship.</span>
              </p>
            </div>

            <MemoryAdmissionLine admission={memoryAdmission} align="start" />
          </div>
          
          {/* Actions */}
          <div className="p-6 pt-2 space-y-3">
            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={isLoading}
              data-testid="voice-privacy-save-button"
              className={`
                w-full py-3 px-4 rounded-xl font-medium text-sm
                flex items-center justify-center gap-2
                transition-all duration-200
                bg-zinc-800 text-white hover:bg-zinc-700
                ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <Save className="w-4 h-4" />
              Save Transcript
            </button>
            
            {/* Let expire button */}
            <button
              onClick={handleLetExpire}
              disabled={isLoading}
              data-testid="voice-privacy-expire-button"
              className={`
                w-full py-3 px-4 rounded-xl font-medium text-sm
                flex items-center justify-center gap-2
                transition-all duration-200
                bg-transparent text-zinc-400 hover:text-zinc-300 hover:bg-zinc-800/50
                ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <Clock className="w-4 h-4" />
              Let It Disappear
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VoicePrivacyModal;
